import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import type {
  DocumentNodeDto,
  DocumentNodeListResponseDto,
  DocumentPresignedUrlResponseDto,
  UploadDocumentResponseDto,
  CreateFolderDto,
  RenameFolderDto,
  MoveNodeDto,
  DiffuseDocumentDto,
  DocumentDiffusionDetailDto,
  DocumentDiffusionStatsDto,
  PendingRecipientsDto,
  AllDiffusionRecipientsDto,
  ResendDocumentDto,
  SendForValidationDto,
  ValidateDocumentDto,
  RejectDocumentDto,
  DocumentValidationStatusDto,
  ListDocumentVersionsResponseDto,
  ListFolderOldVersionsResponseDto,
  ListArchivedDocumentsResponseDto,
  ListLatestVersionsResponseDto,
  NodeDeletionPreviewDto,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { queryClient } from '../lib/queryClient';
import { invalidateDocumentWorkflow } from './documentInvalidation';

const documentsApi = {
  listNodes: async (companyId: string, parentId?: string): Promise<DocumentNodeListResponseDto> => {
    const params: Record<string, string> = {};
    if (parentId) params['parentId'] = parentId;

    const { data: response } = await apiClient.get<DocumentNodeListResponseDto>(
      `/companies/${companyId}/documents`,
      { params },
    );
    return response;
  },

  getNode: async (companyId: string, nodeId: string): Promise<DocumentNodeDto> => {
    const { data: response } = await apiClient.get<DocumentNodeDto>(
      `/companies/${companyId}/documents/${nodeId}`,
    );
    return response;
  },

  createFolder: async (companyId: string, data: CreateFolderDto): Promise<DocumentNodeDto> => {
    const { data: response } = await apiClient.post<DocumentNodeDto>(
      `/companies/${companyId}/documents/folders`,
      data,
    );
    return response;
  },

  renameNode: async (
    companyId: string,
    nodeId: string,
    data: RenameFolderDto,
  ): Promise<DocumentNodeDto> => {
    const { data: response } = await apiClient.patch<DocumentNodeDto>(
      `/companies/${companyId}/documents/${nodeId}/rename`,
      data,
    );
    return response;
  },

  moveNode: async (
    companyId: string,
    nodeId: string,
    data: MoveNodeDto,
  ): Promise<DocumentNodeDto> => {
    const { data: response } = await apiClient.patch<DocumentNodeDto>(
      `/companies/${companyId}/documents/${nodeId}/move`,
      data,
    );
    return response;
  },

  deleteNode: async (companyId: string, nodeId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/documents/${nodeId}`);
  },

  getDeletionPreview: async (
    companyId: string,
    nodeId: string,
  ): Promise<NodeDeletionPreviewDto> => {
    const { data: response } = await apiClient.get<NodeDeletionPreviewDto>(
      `/companies/${companyId}/documents/${nodeId}/deletion-preview`,
    );
    return response;
  },

  getPresignedUrl: async (
    companyId: string,
    nodeId: string,
  ): Promise<DocumentPresignedUrlResponseDto> => {
    const { data: response } = await apiClient.get<DocumentPresignedUrlResponseDto>(
      `/companies/${companyId}/documents/${nodeId}/presigned-url`,
    );
    return response;
  },

  uploadDocument: async (
    companyId: string,
    data: { name: string; parentId: string | null; file: File },
  ): Promise<UploadDocumentResponseDto> => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.parentId) {
      formData.append('parentId', data.parentId);
    }
    formData.append('file', data.file);

    const { data: response } = await apiClient.post<UploadDocumentResponseDto>(
      `/companies/${companyId}/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response;
  },

  diffuseDocument: async (
    companyId: string,
    documentId: string,
    data: DiffuseDocumentDto,
  ): Promise<DocumentDiffusionDetailDto> => {
    const { data: response } = await apiClient.post<DocumentDiffusionDetailDto>(
      `/companies/${companyId}/documents/${documentId}/diffusions`,
      data,
    );
    return response;
  },

  getDiffusionStats: async (
    companyId: string,
    documentId: string,
  ): Promise<DocumentDiffusionStatsDto> => {
    const { data: response } = await apiClient.get<DocumentDiffusionStatsDto>(
      `/companies/${companyId}/documents/${documentId}/diffusions/stats`,
    );
    return response;
  },

  getPendingRecipients: async (
    companyId: string,
    documentId: string,
  ): Promise<PendingRecipientsDto> => {
    const { data: response } = await apiClient.get<PendingRecipientsDto>(
      `/companies/${companyId}/documents/${documentId}/diffusions/pending`,
    );
    return response;
  },

  getAllRecipients: async (
    companyId: string,
    documentId: string,
  ): Promise<AllDiffusionRecipientsDto> => {
    const { data: response } = await apiClient.get<AllDiffusionRecipientsDto>(
      `/companies/${companyId}/documents/${documentId}/diffusions/recipients`,
    );
    return response;
  },

  resendDocument: async (
    companyId: string,
    documentId: string,
    data: ResendDocumentDto,
  ): Promise<DocumentDiffusionDetailDto> => {
    const { data: response } = await apiClient.post<DocumentDiffusionDetailDto>(
      `/companies/${companyId}/documents/${documentId}/diffusions/resend`,
      data,
    );
    return response;
  },

  sendForValidation: async (
    companyId: string,
    documentId: string,
    data: SendForValidationDto,
  ): Promise<DocumentValidationStatusDto> => {
    const { data: response } = await apiClient.post<DocumentValidationStatusDto>(
      `/companies/${companyId}/documents/${documentId}/validation/send`,
      data,
    );
    return response;
  },

  validateDocument: async (
    companyId: string,
    documentId: string,
    data: ValidateDocumentDto,
  ): Promise<DocumentValidationStatusDto> => {
    const { data: response } = await apiClient.post<DocumentValidationStatusDto>(
      `/companies/${companyId}/documents/${documentId}/validation/validate`,
      data,
    );
    return response;
  },

  getValidationStatus: async (
    companyId: string,
    documentId: string,
  ): Promise<DocumentValidationStatusDto> => {
    const { data: response } = await apiClient.get<DocumentValidationStatusDto>(
      `/companies/${companyId}/documents/${documentId}/validation`,
    );
    return response;
  },

  rejectDocument: async (
    companyId: string,
    documentId: string,
    data: RejectDocumentDto,
  ): Promise<DocumentValidationStatusDto> => {
    const { data: response } = await apiClient.post<DocumentValidationStatusDto>(
      `/companies/${companyId}/documents/${documentId}/validation/reject`,
      data,
    );
    return response;
  },

  uploadNewVersion: async (
    companyId: string,
    documentId: string,
    file: File,
  ): Promise<UploadDocumentResponseDto> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data: response } = await apiClient.post<UploadDocumentResponseDto>(
      `/companies/${companyId}/documents/${documentId}/versions`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response;
  },

  listVersions: async (
    companyId: string,
    documentId: string,
  ): Promise<ListDocumentVersionsResponseDto> => {
    const { data: response } = await apiClient.get<ListDocumentVersionsResponseDto>(
      `/companies/${companyId}/documents/${documentId}/versions`,
    );
    return response;
  },

  getVersionPresignedUrl: async (
    companyId: string,
    documentId: string,
    versionId: string,
  ): Promise<DocumentPresignedUrlResponseDto> => {
    const { data: response } = await apiClient.get<DocumentPresignedUrlResponseDto>(
      `/companies/${companyId}/documents/${documentId}/versions/${versionId}/url`,
    );
    return response;
  },

  listFolderOldVersions: async (
    companyId: string,
    parentId?: string,
  ): Promise<ListFolderOldVersionsResponseDto> => {
    const params: Record<string, string> = {};
    if (parentId) params['parentId'] = parentId;

    const { data: response } = await apiClient.get<ListFolderOldVersionsResponseDto>(
      `/companies/${companyId}/documents/versions/old`,
      { params },
    );
    return response;
  },

  archiveDocument: async (companyId: string, documentId: string): Promise<void> => {
    await apiClient.post(`/companies/${companyId}/documents/${documentId}/archive`);
  },

  listArchivedDocuments: async (companyId: string): Promise<ListArchivedDocumentsResponseDto> => {
    const { data: response } = await apiClient.get<ListArchivedDocumentsResponseDto>(
      `/companies/${companyId}/documents/archived`,
    );
    return response;
  },

  listLatestVersions: async (companyId: string): Promise<ListLatestVersionsResponseDto> => {
    const { data: response } = await apiClient.get<ListLatestVersionsResponseDto>(
      `/companies/${companyId}/documents/latest-versions`,
    );
    return response;
  },
};

export const useListNodes = (
  companyId: string,
  parentId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['document-nodes', companyId, parentId ?? 'root'],
    queryFn: () => documentsApi.listNodes(companyId, parentId),
    enabled: !!companyId && (options?.enabled ?? true),
  });
};

export const useGetNode = (companyId: string, nodeId: string) => {
  return useQuery({
    queryKey: ['document-node', companyId, nodeId],
    queryFn: () => documentsApi.getNode(companyId, nodeId),
    enabled: !!companyId && !!nodeId,
  });
};

export const useCreateFolder = (companyId: string) => {
  return useMutation({
    mutationFn: (data: CreateFolderDto) => documentsApi.createFolder(companyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['document-nodes', companyId, variables.parentId ?? 'root'],
      });
      toast.success(i18next.t('documents.create_folder_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useRenameNode = (companyId: string) => {
  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: RenameFolderDto }) =>
      documentsApi.renameNode(companyId, nodeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-nodes', companyId] });
      toast.success(i18next.t('documents.rename_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useMoveNode = (companyId: string) => {
  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: MoveNodeDto }) =>
      documentsApi.moveNode(companyId, nodeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-nodes', companyId] });
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useDeleteNode = (companyId: string) => {
  return useMutation({
    mutationFn: (nodeId: string) => documentsApi.deleteNode(companyId, nodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-nodes', companyId] });
      toast.success(i18next.t('documents.delete_document_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useGetNodeDeletionPreview = (companyId: string, nodeId: string | null) => {
  return useQuery({
    queryKey: ['node-deletion-preview', companyId, nodeId],
    queryFn: () => documentsApi.getDeletionPreview(companyId, nodeId ?? ''),
    enabled: !!companyId && !!nodeId,
  });
};

export const useGetDocumentPresignedUrl = (companyId: string) => {
  return useMutation({
    mutationFn: (nodeId: string) => documentsApi.getPresignedUrl(companyId, nodeId),
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useUploadDocument = (companyId: string) => {
  return useMutation({
    mutationFn: (data: { name: string; parentId: string | null; file: File }) =>
      documentsApi.uploadDocument(companyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['document-nodes', companyId, variables.parentId ?? 'root'],
      });
      toast.success(i18next.t('documents.upload_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useDiffuseDocument = (companyId: string) => {
  return useMutation({
    mutationFn: ({
      documentId,
      groupIds,
      dueDate,
    }: {
      documentId: string;
      groupIds: string[];
      dueDate?: string;
    }) =>
      documentsApi.diffuseDocument(companyId, documentId, {
        groupIds,
        ...(dueDate ? { dueDate } : {}),
      }),
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('documents.diffuse_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useGetDiffusionStats = (companyId: string, documentId: string) => {
  return useQuery({
    queryKey: ['diffusion-stats', companyId, documentId],
    queryFn: () => documentsApi.getDiffusionStats(companyId, documentId),
    enabled: !!companyId && !!documentId,
  });
};

export const useGetPendingRecipients = (companyId: string, documentId: string) => {
  return useQuery({
    queryKey: ['pending-recipients', companyId, documentId],
    queryFn: () => documentsApi.getPendingRecipients(companyId, documentId),
    enabled: !!companyId && !!documentId,
  });
};

export const useGetAllRecipients = (companyId: string, documentId: string) => {
  return useQuery({
    queryKey: ['all-recipients', companyId, documentId],
    queryFn: () => documentsApi.getAllRecipients(companyId, documentId),
    enabled: !!companyId && !!documentId,
  });
};

export const useResendDocument = (companyId: string) => {
  return useMutation({
    mutationFn: ({
      documentId,
      userCompanyIds,
    }: {
      documentId: string;
      userCompanyIds: string[];
    }) => documentsApi.resendDocument(companyId, documentId, { userCompanyIds }),
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('documents.resend_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useSendForValidation = (companyId: string) => {
  return useMutation({
    mutationFn: ({
      documentId,
      validatorIds,
      dueDate,
    }: {
      documentId: string;
      validatorIds: string[];
      dueDate?: string;
    }) =>
      documentsApi.sendForValidation(companyId, documentId, {
        validatorIds,
        ...(dueDate ? { dueDate } : {}),
      }),
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('documents.send_for_validation_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useValidateDocument = (companyId: string) => {
  return useMutation({
    mutationFn: ({ documentId, comment }: { documentId: string; comment?: string }) => {
      const data: ValidateDocumentDto = {};
      if (comment !== undefined) {
        data.comment = comment;
      }
      return documentsApi.validateDocument(companyId, documentId, data);
    },
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('documents.validate_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useGetValidationStatus = (companyId: string, documentId: string) => {
  return useQuery({
    queryKey: ['validation-status', companyId, documentId],
    queryFn: () => documentsApi.getValidationStatus(companyId, documentId),
    enabled: !!companyId && !!documentId,
  });
};

export const useRejectDocument = (companyId: string) => {
  return useMutation({
    mutationFn: ({ documentId, reason }: { documentId: string; reason: string }) =>
      documentsApi.rejectDocument(companyId, documentId, { reason }),
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('documents.reject_document_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useUploadNewVersion = (companyId: string) => {
  return useMutation({
    mutationFn: ({ documentId, file }: { documentId: string; file: File }) =>
      documentsApi.uploadNewVersion(companyId, documentId, file),
    onSuccess: () => {
      invalidateDocumentWorkflow(companyId);
      queryClient.invalidateQueries({ queryKey: ['document-versions', companyId] });
      queryClient.invalidateQueries({ queryKey: ['latest-versions', companyId] });
      toast.success(i18next.t('documents.upload_new_version_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useListVersions = (companyId: string, documentId: string) => {
  return useQuery({
    queryKey: ['document-versions', companyId, documentId],
    queryFn: () => documentsApi.listVersions(companyId, documentId),
    enabled: !!companyId && !!documentId,
  });
};

export const useGetVersionPresignedUrl = (companyId: string) => {
  return useMutation({
    mutationFn: ({ documentId, versionId }: { documentId: string; versionId: string }) =>
      documentsApi.getVersionPresignedUrl(companyId, documentId, versionId),
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useListFolderOldVersions = (
  companyId: string,
  parentId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['folder-old-versions', companyId, parentId ?? 'root'],
    queryFn: () => documentsApi.listFolderOldVersions(companyId, parentId),
    enabled: !!companyId && (options?.enabled ?? true),
  });
};

export const useArchiveDocument = (companyId: string) => {
  return useMutation({
    mutationFn: (documentId: string) => documentsApi.archiveDocument(companyId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-nodes', companyId] });
      queryClient.invalidateQueries({ queryKey: ['archived-documents', companyId] });
      queryClient.invalidateQueries({ queryKey: ['latest-versions', companyId] });
      invalidateDocumentDashboards(companyId);
      toast.success(i18next.t('documents.archive_document_modal.success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useListArchivedDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['archived-documents', companyId],
    queryFn: () => documentsApi.listArchivedDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useListLatestVersions = (companyId: string) => {
  return useQuery({
    queryKey: ['latest-versions', companyId],
    queryFn: () => documentsApi.listLatestVersions(companyId),
    enabled: !!companyId,
  });
};
